/** PM Polling — WebSocket-gated AJAX updates, read receipts, member stats.
 *
 * Instead of polling every 6 seconds unconditionally, polls are triggered by
 * WebSocket events (new message, counter update, sync). The SAFETY_NET_INTERVAL
 * timer catches anything missed. Polls at FALLBACK_INTERVAL until the WebSocket
 * connects and after it disconnects. Neither timer runs while the tab is hidden.
 */

// Polling mode: 'ws-gated' or 'fallback' (no WebSocket). Starts in fallback so
// a page whose socket is never created (no token) still polls at FALLBACK_INTERVAL.
var _pollMode = 'fallback';

/** Poll mode as the `m` request param, counted server-side by poll rate. */
function pollModeParam() {
  return _pollMode === 'ws-gated' ? 'ws' : 'poll';
}

/**
 * Poll trigger as the `trigger` request param. `timer` is the poll scheduled
 * at the mode's interval; every other request is `event`.
 */
function pollTriggerParam(trigger) {
  return trigger === 'timer' ? 'timer' : 'event';
}
var _safetyNetTimer = null;
var _debouncePollTimer = null;
var _pollInFlight = false;
// Set by deferIfInFlight, so finishPoll re-issues the poll. The in-flight poll
// may have read the server before the event's change committed.
var _requestPollPending = false;
// Sticky "rebuild the thread list" intent. A reaction does not bump max_msg_id,
// so it asks for a forced rebuild. If a poll is mid-flight the request is
// deferred, and the flag must survive the deferred poll (which carries no
// argument) so the forced rebuild is not silently downgraded to a normal poll.
var _forceThreadsPending = false;
// Counts forced-rebuild requests. A response consumes _forceThreadsPending only
// when no request arrived after it was sent, since it cannot carry a later one.
var _forceThreadsRequests = 0;
// Monotonic poll counter. Each poll request stamps the value it issued under so
// a response that a newer poll (or a filter switch) has superseded is discarded
// instead of patching a list it no longer matches.
var _pollSeq = 0;
// Last pmNewCnt this socket delivered. null until known, and an unknown value polls.
var _pmNewCnt = null;
// Thread ids removed locally in the last few seconds. A poll that sampled the
// server just before a delete committed can carry a stale fragment for the
// just-removed thread. This guard makes the delete win: reconcile skips any
// changed row whose id is here.
window._recentlyRemoved = window._recentlyRemoved || {};
const SAFETY_NET_INTERVAL = 180000;
const FALLBACK_INTERVAL = 10000;
const DEBOUNCE_DELAY = 500;

/**
 * Record a poll requested while another is in flight. A 1 s retry would share
 * _safetyNetTimer, which finishPoll replaces, so finishPoll re-issues it.
 */
function deferIfInFlight() {
  if (!_pollInFlight) return false;
  _requestPollPending = true;
  return true;
}

/**
 * Release the in-flight guard and schedule the next poll at the mode interval,
 * then re-issue a requestPoll that arrived during the poll.
 * Runs on every terminal outcome of a poll request: success, HTTP or network
 * failure, and a response handler that throws.
 */
function finishPoll() {
  _pollInFlight = false;
  window.run_update_process();
  if (_requestPollPending) {
    _requestPollPending = false;
    window.requestPoll();
  }
}

/**
 * Issue one /app/messages/update request. onResponse receives the parsed body
 * on success and finishPoll runs on every terminal outcome. Do not move the
 * reset into .always(): jQuery fires callbacks without a try/catch, so a
 * .done() handler that throws skips every callback registered after it.
 */
function pollRequest(params, onResponse) {
  $.ajax({
    type: "POST",
    url: "/app/messages/update",
    data: params,
    dataType: "json"
  }).done(function(data) {
    try {
      onResponse(data);
    } catch (e) {
      // A response that did not apply leaves the count unknown.
      _pmNewCnt = null;
      throw e;
    } finally {
      finishPoll();
    }
  }).fail(function() {
    // The rise this poll answered may be unshown, so the next pmNewCnt frame polls.
    _pmNewCnt = null;
    finishPoll();
  });
}

window.append_msgs = function(msg_data, target) {
  let msgs_jq = jQuery(msg_data);

  // Collapse messages from same user
  let last_user = $("#threads_right").find(".msg_username:last").text();
  if (last_user === msgs_jq.find(".msg_username:first").text()) {
    msgs_jq.find(".msg_meta_wrapper:first").remove();
    // Show inline actions that were hidden because header was present
    msgs_jq.filter(".pm_msg:first").find(".msg-inline-actions").removeClass("hidden");
    // Tighten spacing for consecutive same-sender messages
    msgs_jq.filter(".pm_msg:first").removeClass("mt-3").addClass("mt-0.5");
  }

  // With column-reverse, scrollTop=0 is the bottom.
  // Check if user is near bottom BEFORE append (scrollHeight changes after).
  var el = document.getElementById('threads_right');
  var wasAtBottom = !el || Math.abs(el.scrollTop) < 80;

  // Appending re-anchors the column-reverse list, moving every row the open
  // dropdown is positioned against. When the thread is already pinned to the
  // bottom no scroll event fires, so dismiss the menu here as well.
  window.closeMessageActionMenus();

  var wrapper = target.find('#messages_wrapper');
  var appendTarget = wrapper.length ? wrapper : target;
  // Paged view keeps its page control after the last message (pm/pagination.js).
  var pagedControls = appendTarget.children('#paged-view-controls');
  if (pagedControls.length) {
    msgs_jq.insertBefore(pagedControls);
  } else {
    appendTarget.append(msgs_jq);
  }

  // Initialize Lucide icons in the newly appended content
  msgs_jq.each(function() {
    if (window.LucideIcons && this.nodeType === 1) {
      window.LucideIcons.initialize(this);
    }
  });

  // If conversation empty, remove no_messages indicator
  if (target.find(".no_messages").length) {
    target.find(".no_messages").remove();
  }

  // Consolidate read receipt checkmarks
  if (window.consolidateReadReceipts) {
    window.consolidateReadReceipts();
  }

  // With column-reverse, new content at the end of the wrapper appears at
  // the visual bottom. scrollTop=0 keeps us pinned there.
  if (wasAtBottom && el) {
    el.scrollTop = 0;
  }
};

window.update_pm_info_paged = function(forceThreads, trigger) {
  if (forceThreads) {
    _forceThreadsPending = true;
    _forceThreadsRequests++;
  }
  if (deferIfInFlight()) return;
  if (window.pm_ajax_request_active) {
    // Run process again in 1 sec (the pending force flag and trigger survive the retry).
    window.run_update_process(1000, trigger);
    return;
  }
  // The thread list exists only on /app/messages. This module ships in the
  // global bundle, so a poll triggered from any other page has nothing to
  // update (header badges are driven by websocket-client.js) and the DOM
  // writes below would dereference missing elements.
  if (!document.getElementById("threads_left")) {
    return;
  }
  _pollInFlight = true;

  // If not on first page we dont update threads and messages list
  if (window.get_active_page() != 1) {
    finishPoll();
    return;
  }

  let stw = $("#single_thread_wrapper"),
    viewing_thread = stw.length > 0,
    tl = $("#threads_left"),
    thisSeq = ++_pollSeq,
    forceRequestsAtSend = _forceThreadsRequests,
    options = {
      // TS for user stats
      "ts": window.cur_ts,
      // Poll mode and trigger, counted server-side per pair
      "m": pollModeParam(),
      "trigger": pollTriggerParam(trigger)
    };

  if (viewing_thread) {
    // Thread id to get new messages
    options.thread = window.get_active_thread_id();
    // Find newest message
    options.newest_msg_id = window.get_newest_msg_id_in_thread();
  } else {
    // max_msg_id to get thread list changes
    options.max_msg_id = tl.data("max-msg-id");
    // Opt in to the row delta (only changed rows); see update_pm_info.
    options.delta = 1;
    // Rebuild the thread list even when max_msg_id is unchanged (e.g. a
    // reaction changed an inbox snippet without a new message).
    options.force_threads = _forceThreadsPending ? 1 : 0;
  }

  pollRequest(options, function(data) {
    // Auth/CSRF/onboarding gate returns {redirect: url} for a lapsed session or
    // stale token. The global ajaxComplete handler navigates, so just stop here.
    if (data && typeof data.redirect === 'string' && data.redirect) {
      return;
    }
    if (data?.not_logged_in == 1) {
      window.location.href = window.location.protocol + "//" +
        window.location.hostname + window.location.pathname;
      return;
    }

    // Update member stats if any
    if (typeof data.member_stats != "undefined") {
      window.update_member_stats_messages(data.member_stats);
    }

    let c = $("#conversation");

    if (viewing_thread) {
      // If new messages arrived
      if (typeof data.new_messages != "undefined" && data.new_messages.length > 0) {
        window.messages_highlighted = 1;
        // Remove "new" in 3 seconds
        window.setTimeout(function() {
          window.remove_new_mark();
        }, 3000);

        c.prepend(data.new_messages);
        // If more than 25 msgs - remove
        c.find(".pm_msg:gt(24)").remove();
      }

      // Apply the contact's read pointer
      if (typeof data.contact_last_read_msg_id != "undefined") {
        window.applyContactRead(options.thread, data.contact_last_read_msg_id);
      }
    } else {
      // Discard if a newer poll superseded this one (paged sidebar has no
      // filter switch, so the seq check is the only staleness guard here).
      var listStale = thisSeq !== _pollSeq;
      // Consume the forced-rebuild intent only when we apply the response, so a
      // discarded stale poll keeps the flag set for the next poll.
      if (!listStale && _forceThreadsRequests === forceRequestsAtSend) {
        _forceThreadsPending = false;
      }
      if (!listStale && data.threads_delta) {
        // Delta path: patch only changed rows in place.
        window.reconcileThreadRows(data.threads_delta);
      } else if (!listStale && data.threads_update && data.max_msg_id) {
        // Full-rebuild fallback.
        tl.data("max-msg-id", data.max_msg_id);
        tl.empty();
        tl.append(data.threads_update);
        if (window.LucideIcons) {
          window.LucideIcons.initialize(tl[0]);
        }
        if (window.initializeThreadActions) {
          window.initializeThreadActions();
        }
      }
    }
  });
};

window.update_pm_info = function(forceThreads, trigger) {
  if (forceThreads) {
    _forceThreadsPending = true;
    _forceThreadsRequests++;
  }
  if (deferIfInFlight()) return;
  // Any request's always() clears pm_ajax_request_active, so a paged load holds polls
  // with its own flag.
  if (window.pm_ajax_request_active || window.pagedViewLoadPending) {
    // Run process again in 1 sec (the pending force flag and trigger survive the retry).
    window.run_update_process(1000, trigger);
    return;
  }
  // Off the /app/messages page there is no thread list to update (see the
  // matching guard in update_pm_info_paged).
  if (!document.getElementById("threads_left")) {
    return;
  }
  _pollInFlight = true;

  let threadsLeft = $("#threads_left"),
    filtersBlock = $("#filters"),
    filter = filtersBlock.data("filter"),
    newestMsgIdInThread = window.get_newest_msg_id_in_thread(),
    thisSeq = ++_pollSeq,
    forceRequestsAtSend = _forceThreadsRequests,
    pagedSeqAtSend = window.pagedViewRequestSeq;

  pollRequest({
    // Send this to get new messages
    "thread": window.get_active_thread_id(),
    // Active filter
    "filter": filter,
    // Find newest message
    "newest_msg_id": newestMsgIdInThread,
    // Send this to get thread list changes
    "max_msg_id": threadsLeft.data("max-msg-id"),
    "threads_count": threadsLeft.find(".pm_thread").length,
    // Opt in to the row delta (only changed rows). When a delta can't be
    // produced the server returns a full threads_update instead, which the
    // client detects by the absence of threads_delta.
    "delta": 1,
    // Rebuild the thread list even when max_msg_id is unchanged (e.g. a
    // reaction changed an inbox snippet without a new message).
    "force_threads": _forceThreadsPending ? 1 : 0,
    // TS for user stats
    "ts": window.cur_ts,
    // Poll mode and trigger, counted server-side per pair
    "m": pollModeParam(),
    "trigger": pollTriggerParam(trigger)
  }, function(data) {
    // Auth/CSRF/onboarding gate returns {redirect: url} for a lapsed session or
    // stale token. The global ajaxComplete handler navigates, so just stop here.
    if (data && typeof data.redirect === 'string' && data.redirect) {
      return;
    }
    // cast as int
    if (data.not_logged_in == 1) {
      window.location.href = window.location.protocol + "//" +
        window.location.hostname + window.location.pathname;
      return;
    }

    // Update member stats if any
    if (typeof data.member_stats != "undefined") {
      window.update_member_stats_messages(data.member_stats);
    }

    // Discard a thread-list update the user has moved past: a newer poll has
    // been issued, or the filter changed since this request went out. Patches
    // and the cursor are both dropped so the next same-filter poll re-fetches
    // and re-applies (one redundant fetch, never a stale or skipped row).
    var listStale = (thisSeq !== _pollSeq) || (filtersBlock.data("filter") !== filter);

    // The forced rebuild (if any) was carried by this request, but only consume
    // the pending flag when we actually apply the response. A discarded stale
    // poll keeps the flag set so the next same-filter poll re-forces the rebuild.
    if (!listStale && _forceThreadsRequests === forceRequestsAtSend) {
      _forceThreadsPending = false;
    }

    if (!listStale && data.threads_delta) {
      // Delta path: patch only the changed rows in place, no list teardown.
      window.reconcileThreadRows(data.threads_delta);
    } else if (!listStale && data.threads_update && data.max_msg_id) {
      // Full-rebuild fallback (cold cursor, too many changes, kill-switch off,
      // reaction-only force, or a non-'all' filter).
      // Update 'max_msg_id'
      threadsLeft.data("max-msg-id", data.max_msg_id);

      let activeThreadId = window.get_active_thread_id();
      let savedScrollTop = threadsLeft[0].scrollTop;
      // Parse new content into a fragment, then swap in one operation to avoid blink
      var tmp = document.createElement('div');
      tmp.innerHTML = data.threads_update;
      var frag = document.createDocumentFragment();
      while (tmp.firstChild) frag.appendChild(tmp.firstChild);
      while (threadsLeft[0].firstChild) {
        threadsLeft[0].removeChild(threadsLeft[0].firstChild);
      }
      threadsLeft[0].appendChild(frag);
      threadsLeft[0].scrollTop = savedScrollTop;
      requestAnimationFrame(function() { threadsLeft[0].scrollTop = savedScrollTop; });

      // Initialize Lucide icons in the replaced thread list
      if (window.LucideIcons) {
        window.LucideIcons.initialize(threadsLeft[0]);
      }

      // Re-initialize thread action buttons (desktop dropdown vs mobile swipe)
      if (window.initializeThreadActions) {
        window.initializeThreadActions();
      }

      // Reset infinite scroll state after thread list replacement
      window._threadsFullyLoaded = !document.getElementById('load_more_conversations');

      // Scoped to the rebuilt list. A Message Request's row is carried outside
      // it (pm/index.twig) and is not a Primary conversation to re-activate.
      if (activeThreadId && threadsLeft.find("#thread_" + activeThreadId).length) {
        // If threads was reloaded and there was active thread -
        // set active thread again, without reloading messages
        window.view_thread(activeThreadId, false);
        threadsLeft[0].scrollTop = savedScrollTop;
        setTimeout(function() { threadsLeft[0].scrollTop = savedScrollTop; }, 0);
        setTimeout(function() { threadsLeft[0].scrollTop = savedScrollTop; }, 50);
      }
    }

    let activeThreadId1 = window.get_active_thread_id();
    // Update only if thread_id same
    if (typeof data.thread_id == "undefined" || data.thread_id !== activeThreadId1) return;

    // Apply the contact's read pointer to the open pane and its inbox row
    if (typeof data.contact_last_read_msg_id != "undefined") {
      window.applyContactRead(activeThreadId1, data.contact_last_read_msg_id);
    }

    // Update header status from server response
    if (typeof data.contact_status != "undefined") {
      window.update_header_status(data.contact_status);
    }

    // Hide compose box and update header when the contact is inactive/deleted
    if (data.contact_active === false) {
      $("#threads_right_bottom").addClass("hidden");
      if (data.contact_name) {
        $("#current_thread_username").text(data.contact_name);
      }
    }

    // If new messages arrived
    if (data.new_messages) {
      if (!document.hasFocus() && typeof window.flashTitle === 'function') {
        window.flashTitle('**** ' + window.t('legacy.pm.new_message_alert') + ' ****');
      }

      // A paged load or a pane change after this poll went out makes its messages stale
      // (pm/pagination.js). The next poll fetches them again.
      if (window.pagedViewRequestSeq !== pagedSeqAtSend) {
        // Runs after finishPoll clears _pollInFlight and schedules the next timer poll.
        window.setTimeout(window.requestPoll, 0);
        return;
      }
      // Paged view: only a rendered page 1 takes incoming messages.
      if (window.pagedViewActive && !$('#messages_wrapper[data-paged-page="1"]').length) {
        return;
      }

      window.messages_highlighted = 1;
      // Remove "new" in 3 seconds
      window.setTimeout(function() {
        window.remove_new_mark();
      }, 3000);

      // Find last date divider
      let threadsRight = $("#threads_right"),
        first_datediv = threadsRight.find(".pm_date_div:last");

      // Append new messages to message list
      window.append_msgs(data.new_messages, threadsRight);

      // Remove dup date dividers
      var date = first_datediv.data("date"),
        last_datediv = $("#threads_right").find("div[data-date=" + date + "]:last");
      if (last_datediv.length > 0) {
        last_datediv.remove();
      }

    }
  });
};

window.remove_new_mark = function() {
  if ($("#single_thread_wrapper").length > 0) {
    // Classic view
    $("#conversation").find(".pm_new").removeClass("pm_new");
  } else {
    // Split view
    $("#threads_right").find(".pm_new").removeClass("pm_new");
  }
};

/**
 * Apply a contact read to the open conversation and its inbox row.
 * lastReadMsgId is the id of the last message the contact read: own messages
 * above it stay unread, '0' (never opened, or read receipts hidden) reads
 * nothing.
 */
window.applyContactRead = function(thread_id, lastReadMsgId) {
  lastReadMsgId = String(lastReadMsgId == null ? '' : lastReadMsgId);
  if (!lastReadMsgId || lastReadMsgId === '0') return;
  var active = window.get_active_thread_id ? window.get_active_thread_id() : null;
  if (active != null && String(active) === String(thread_id)) {
    var flipped = false;
    document.querySelectorAll("#threads_right .own_msg.pm_unread").forEach(function(msg) {
      var msgId = String(msg.id || "").replace(/^msg_/, "");
      if (compareMsgId(msgId, lastReadMsgId) > 0) return;
      msg.classList.remove("pm_unread");
      flipped = true;
    });
    if (flipped) {
      window.updateReadReceipts();
    }
  }
  markThreadRowRead(thread_id, lastReadMsgId);
};

// Flip the inbox row's own-message check to the read state. The row shows a
// check only when the last message is the viewer's own (pm/macros.twig).
function markThreadRowRead(thread_id, lastReadMsgId) {
  var row = document.getElementById("thread_" + thread_id);
  if (!row || !(row.dataset && row.dataset.lastMsgId)) return;
  if (compareMsgId(row.dataset.lastMsgId, lastReadMsgId) > 0) return;
  var icon = row.querySelector(".th_snippet > [data-lucide='check']");
  if (icon) markCheckRead(icon);
}

/* lucide-icons: check-check */
function markCheckRead(icon) {
  icon.classList.remove("text-gray-400");
  icon.classList.add("text-sky-400");
  if (window.LucideIcons) {
    window.LucideIcons.swap(icon, "check-check");
  }
}

window.updateReadReceipts = function() {
  // Update unread→read: swap icon and status on wrappers
  document.querySelectorAll("#threads_right .own_msg:not(.pm_unread):not([data-optimistic]) .read-receipt-wrap[data-status='unread']").forEach(function(wrap) {
    wrap.setAttribute("data-status", "read");
    var icon = wrap.querySelector("[data-lucide]");
    if (icon) markCheckRead(icon);
  });
  // Re-consolidate after status changes
  window.consolidateReadReceipts();
};

window.consolidateReadReceipts = function() {
  var msgs = document.querySelectorAll("#messages_wrapper > .pm_msg");
  var run = [];
  var lastStatus = null;

  function flushRun() {
    if (run.length === 0) return;
    for (var i = 0; i < run.length - 1; i++) {
      run[i].classList.add("hidden");
      run[i].classList.remove("flex");
    }
    run[run.length - 1].classList.remove("hidden");
    run[run.length - 1].classList.add("flex");
    run = [];
  }

  msgs.forEach(function(msg) {
    var wrap = msg.querySelector(".read-receipt-wrap");
    if (!wrap) {
      flushRun();
      lastStatus = null;
      return;
    }

    var status = wrap.getAttribute("data-status");

    if (lastStatus !== null && lastStatus !== status) {
      flushRun();
    }

    run.push(wrap);
    lastStatus = status;
  });

  flushRun();
};

/**
 * Schedule the next poll. In ws-gated mode, this sets the safety-net timer
 * (SAFETY_NET_INTERVAL). In fallback mode it sets FALLBACK_INTERVAL.
 * Explicit interval overrides (e.g. retry-in-1s) are honored regardless of mode.
 * A poll scheduled at the mode interval is issued as a `timer` poll. An
 * explicit-interval poll carries `trigger`, the label of the poll it re-issues.
 * While the tab is hidden no mode-interval poll is scheduled. Hiding the tab
 * cancels a pending timer or retry poll, and showing it polls once and resumes.
 */
window.run_update_process = function(interval, trigger) {
  clearTimeout(_safetyNetTimer);
  const updateFn = window.pm_view_mode === "paged" ? window.update_pm_info_paged : window.update_pm_info;

  if (typeof interval != "undefined") {
    // Explicit interval requested (e.g. retry after busy). A retry with no
    // trigger counts as an event.
    _safetyNetTimer = window.setTimeout(function() {
      updateFn(undefined, pollTriggerParam(trigger));
    }, interval);
    return;
  }

  if (document.hidden) return;

  var delay = _pollMode === 'ws-gated' ? SAFETY_NET_INTERVAL : FALLBACK_INTERVAL;
  _safetyNetTimer = window.setTimeout(function() {
    updateFn(undefined, 'timer');
  }, delay);
};

/**
 * Request an immediate poll, debounced to batch rapid WebSocket events.
 * While a poll is in flight, the request waits for finishPoll.
 */
window.requestPoll = function() {
  if (deferIfInFlight()) return;
  clearTimeout(_debouncePollTimer);
  _debouncePollTimer = setTimeout(function() {
    clearTimeout(_safetyNetTimer);
    const updateFn = window.pm_view_mode === "paged" ? window.update_pm_info_paged : window.update_pm_info;
    updateFn(undefined, 'event');
  }, DEBOUNCE_DELAY);
};

/**
 * Whether a COUNTER_UPDATE frame needs a poll: only a pmNewCnt rise or an
 * unknown count. MessageSentEventSubscriber also sends THREAD_NEW_MESSAGE for
 * every rise.
 */
function counterUpdateNeedsPoll(detail) {
  if (!detail || detail.id !== 'pmNewCnt') return false;
  var value = parseInt(detail.value, 10);
  if (!Number.isFinite(value)) return true;
  var previous = _pmNewCnt;
  _pmNewCnt = value;
  return previous === null || value > previous;
}

// ─── Inbox row reconcile ─────────────────────────────────────────────
//
// Patch only the thread rows that changed since the client's cursor, in place,
// instead of tearing down and rebuilding #threads_left. Keeps scroll position,
// keeps rows scrolled past the server's render cap, and skips re-rendering the
// whole list on every message. The full-rebuild path above is the fallback.

// Compare two message ids as bigints (they exceed Number.MAX_SAFE_INTEGER, so
// never parseInt them). Decimal strings: shorter is smaller, equal length sorts
// lexicographically. Returns -1 / 0 / 1.
function compareMsgId(a, b) {
  var sa = String(a == null ? '' : a);
  var sb = String(b == null ? '' : b);
  if (sa.length !== sb.length) return sa.length < sb.length ? -1 : 1;
  if (sa === sb) return 0;
  return sa < sb ? -1 : 1;
}
// Exposed so the node unit test reaches it without depending on vm hoisting.
window.compareMsgId = compareMsgId;

function threadRows(container) {
  return Array.prototype.filter.call(container.children, function(el) {
    return el.classList && el.classList.contains('pm_thread');
  });
}

function rowLastMsgId(node) {
  return (node && node.dataset && node.dataset.lastMsgId) || '0';
}

function parseRowFragment(html) {
  var tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.firstElementChild;
}

function reinitRow(node) {
  if (window.LucideIcons && node) {
    window.LucideIcons.initialize(node);
  }
}

// Insert node so the list stays sorted by last_msg_id DESC: before the first
// row whose id is lower. A new-message row (the new global max) lands at the top.
function insertSorted(container, node, lastMsgId) {
  var rows = threadRows(container);
  for (var i = 0; i < rows.length; i++) {
    if (compareMsgId(rowLastMsgId(rows[i]), lastMsgId) < 0) {
      container.insertBefore(node, rows[i]);
      return;
    }
  }
  // Lower than every loaded row: drop it just above the load-more sentinel so
  // pagination order stays intact, else at the end.
  var sentinel = document.getElementById('load_more_conversations');
  if (sentinel && sentinel.parentNode === container) {
    container.insertBefore(node, sentinel);
  } else {
    container.appendChild(node);
  }
}

// Move each changed row (except the read-only active one, whose position is
// unchanged) to its sorted slot. Only changed nodes move, so unchanged rows
// keep their relative order — the scroll anchor depends on that.
function reorderChanged(container, changed, active, insertedIds) {
  for (var i = 0; i < changed.length; i++) {
    var id = String(changed[i].thread_id);
    if (id === active) continue;
    if (window._recentlyRemoved[id]) continue;
    // A row just inserted in the patch pass is already at its sorted slot.
    if (insertedIds && insertedIds[id]) continue;
    var node = document.getElementById('thread_' + id);
    if (!node || node.parentNode !== container) continue;
    container.removeChild(node);
    insertSorted(container, node, changed[i].last_msg_id);
  }
}

// The conversation header, the call buttons and the profile preview are built
// once from the open row's contact badge. A replaced row can carry a different
// one, and nothing else re-reads it.
function resyncOpenContact(openId, previousBadge) {
  if (!openId || previousBadge === null) return;
  var row = document.getElementById('thread_' + openId);
  var badge = row ? row.getAttribute('data-contact-badge-status') : null;
  if (!badge || badge === previousBadge) return;
  if (window.update_user_header) window.update_user_header(openId);
  if (window.pm_sync_side_panel) window.pm_sync_side_panel($('#thread_' + openId));
}

// Keep .active on (and only on) the currently-open thread. Uses the live active
// id, not a stale one from the response, so a mid-poll thread switch is safe.
function reapplyActive(activeId) {
  var container = document.getElementById('threads_left');
  if (!container) return;
  var active = container.querySelectorAll('.pm_thread.active');
  for (var i = 0; i < active.length; i++) active[i].classList.remove('active');
  if (activeId) {
    var row = document.getElementById('thread_' + activeId);
    if (row) row.classList.add('active');
  }
}

// Record the topmost visible UNCHANGED rows (up to 3) and their offset relative
// to the viewport top, BEFORE any mutation. An unchanged row's own height is
// stable, so restoring it to the same viewport offset keeps the view steady
// while rows above absorb the height change.
function captureScrollAnchor(container, changedIds) {
  if (container.scrollTop <= 2) return { atTop: true };
  var rows = threadRows(container);
  var candidates = [], delta = {}, i, row;
  var viewportBottom = container.scrollTop + container.clientHeight;
  for (i = 0; i < rows.length; i++) {
    row = rows[i];
    // Only anchor on a row that actually intersects the visible viewport, so the
    // restored position keeps an on-screen row stationary (an off-screen anchor
    // could let the visible region shift).
    if (row.offsetTop + row.offsetHeight > container.scrollTop && row.offsetTop < viewportBottom) {
      if (!changedIds[row.dataset.threadId]) {
        candidates.push(row.dataset.threadId);
        delta[row.dataset.threadId] = row.offsetTop - container.scrollTop;
        if (candidates.length >= 3) break;
      }
    }
  }
  // When every visible row is changing (rare), there is no stable anchor: a
  // changed row's height can shift on re-render, so anchoring on one would make
  // the restore offset wrong. Leave candidates empty and let restoreScrollAnchor
  // fall through to clamping the current scrollTop into range.
  return { atTop: false, candidates: candidates, delta: delta };
}

// Restore scroll AFTER mutation, clamped, falling through candidates in case the
// chosen anchor row was removed in the meantime (e.g. by a concurrent delete).
function restoreScrollAnchor(container, anchor) {
  var maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
  if (anchor.atTop) { container.scrollTop = 0; return; }
  for (var i = 0; i < anchor.candidates.length; i++) {
    var id = anchor.candidates[i];
    var row = document.getElementById('thread_' + id);
    if (row && row.parentNode === container) {
      container.scrollTop = Math.max(0, Math.min(Math.round(row.offsetTop - anchor.delta[id]), maxTop));
      return;
    }
  }
  // No anchor survived: at least keep scrollTop in range (never past the end).
  container.scrollTop = Math.max(0, Math.min(container.scrollTop, maxTop));
}

/**
 * Apply a server row delta to #threads_left in place. delta = { changed:
 * [{thread_id, last_msg_id, html}], active_thread_id, max_msg_id }.
 */
window.reconcileThreadRows = function(delta) {
  var container = document.getElementById('threads_left');
  if (!container) return;
  var changed = delta.changed || [];
  var active = delta.active_thread_id != null ? String(delta.active_thread_id) : null;

  var changedIds = {};
  for (var i = 0; i < changed.length; i++) changedIds[String(changed[i].thread_id)] = true;

  var anchor = captureScrollAnchor(container, changedIds);

  var openId = window.get_active_thread_id ? window.get_active_thread_id() : null;
  openId = openId != null ? String(openId) : null;
  var openRow = openId ? document.getElementById('thread_' + openId) : null;
  var openBadgeBefore = openRow ? openRow.getAttribute('data-contact-badge-status') : null;

  // 1. Patch existing rows / insert new ones. One node per thread id.
  var insertedIds = {};
  for (var k = 0; k < changed.length; k++) {
    var c = changed[k];
    var id = String(c.thread_id);
    // A local delete wins over a stale in-flight delta re-adding the row.
    if (window._recentlyRemoved[id]) continue;
    var node = parseRowFragment(c.html);
    if (!node) continue;
    var existing = document.getElementById('thread_' + id);
    if (existing) {
      existing.replaceWith(node);
      reinitRow(node);
    } else if (id === active) {
      // Read-only active change with no loaded row (active thread below the
      // floor): nothing on-screen to update, so skip rather than inject a
      // duplicate that would also live deep in the list.
      continue;
    } else {
      insertSorted(container, node, c.last_msg_id);
      reinitRow(node);
      insertedIds[id] = true;
    }
  }

  // 2. Move changed rows to their sorted slots. Newly inserted rows are already
  //    at the right position, and the read-only active row stays put.
  reorderChanged(container, changed, active, insertedIds);

  // 3. Rebind action buttons once (handlers are delegated; this toggles the
  //    desktop-dropdown vs mobile-swipe affordance on the touched rows).
  if (window.initializeThreadActions) window.initializeThreadActions();

  // 4. Advance the cursor (jQuery .data, matching the rest of polling).
  if (delta.max_msg_id) $(container).data('max-msg-id', delta.max_msg_id);

  // 5. Keep the active highlight on the open thread; never reload its messages.
  reapplyActive(openId);
  resyncOpenContact(openId, openBadgeBefore);

  // 6. Restore scroll with a single clamped write.
  restoreScrollAnchor(container, anchor);
};

/**
 * Remove a thread row in place (cross-device delete / blocked-by-other-user),
 * anchoring scroll so a removal above the viewport doesn't shift visible rows.
 */
window.removeThreadRow = function(threadId) {
  var id = String(threadId);
  // Guard a brief window so a stale in-flight delta can't re-add the row.
  window._recentlyRemoved[id] = true;
  setTimeout(function() { delete window._recentlyRemoved[id]; }, 5000);

  // Notify the Message Requests UI (#3039) so it drops the matching request row
  // and updates its count. Fired even when there is no #thread_<id> in the
  // Primary list (request list rows live in their own containers).
  try {
    document.dispatchEvent(new CustomEvent('pm:thread-removed', { detail: { threadId: id } }));
  } catch (e) {}

  var container = document.getElementById('threads_left');
  var row = container ? document.getElementById('thread_' + id) : null;
  if (!container || !row) return;

  var anchor = captureScrollAnchor(container, {});
  row.remove();

  // Close the open pane if it was showing the removed thread (mirrors the
  // local delete close in crud.js).
  if (window.get_active_thread_id && String(window.get_active_thread_id()) === id) {
    if (window.pm_view_mode === 'chat') {
      $('#threads_main').removeClass('threads--open');
      if (typeof $.scrollTo === 'function' && $('.cCont').length) {
        $.scrollTo($('.cCont'));
      }
      window.abandonPagedLoad?.();
      $('#threads_right').empty();
    }
  }

  restoreScrollAnchor(container, anchor);
};

// ─── Message recall (unsend) removal ──────────────────────────
//
// The append-only message poll only carries *new* messages, so a recall of an
// already-rendered bubble needs a dedicated path. The realtime
// THREAD_MESSAGE_RECALLED event (websocket-client.js) dispatches
// 'thread:recalled'; this removes the matching #msg_{id} bubble. The sender's
// own removal is driven by the API response, so this handler is idempotent
// (no-op if the bubble is already gone).

/**
 * Remove a recalled (unsent) message bubble from the open conversation.
 * Idempotent: a bubble already removed is a no-op. message_id / thread_id are
 * strings (they exceed Number.MAX_SAFE_INTEGER).
 *
 * Sibling: applyRecallRemoval() in pm/crud.js drives the same removal for the
 * sender's own API-response path.
 */
window.applyMessageRecallRemoval = function(message_id, thread_id) {
  // A peer unsend arrives without any gesture from this user, so no click or
  // scroll dismisses the dropdown before the row it is anchored against goes.
  window.closeMessageActionMenus();

  var node = document.getElementById('msg_' + String(message_id));
  if (node) {
    node.remove();
  }

  // Refresh the thread-list snippet: recall rolls it back to the prior message
  // server-side, and the delta poll picks that up.
  if (window.requestPoll) {
    window.requestPoll();
  }
};

// ─── Message edit (live body swap) ──────────────────────────
//
// The append-only message poll only carries *new* messages, so an edit of an
// already-rendered bubble needs a dedicated path. The realtime
// THREAD_MESSAGE_EDITED event (websocket-client.js) dispatches 'thread:edited';
// this swaps the bubble's text region with the server-rendered body. The
// sender's own tab is also driven by this event (the API response applies a
// plain-text interim render only), so the handler is idempotent (re-assigning
// the already-current HTML is a no-op).

/**
 * Swap an edited message bubble's text region in the open conversation and
 * reveal its "(edited)" tag. message_id / thread_id are strings (they exceed
 * Number.MAX_SAFE_INTEGER). Only the .msg-body-text region is touched, so a
 * reply's quote block stays intact.
 */
window.applyMessageEditUpdate = function(detail) {
  var activeThreadId = window.get_active_thread_id ? window.get_active_thread_id() : null;
  if (activeThreadId && String(activeThreadId) === String(detail.thread_id)) {
    var node = document.getElementById('msg_' + String(detail.message_id));
    if (node) {
      var textRegion = node.querySelector('.msg-body-text');
      if (textRegion) {
        textRegion.innerHTML = detail.body_html || '';
      }

      // Mark that the authoritative server-rendered body has landed. The
      // sender's inline editor (pm/crud.js) checks this to decide whether to
      // apply a plaintext interim render, so the socket render always wins
      // when it arrives around the fetch response.
      node.setAttribute('data-edit-swapped', '1');

      // Emoji-only styling only applies to plain-text bubbles (a reply keeps
      // its quote block and regular bubble chrome).
      var body = node.querySelector('.msg_body');
      if (body && !body.querySelector('.reply-quote-block')) {
        body.classList.toggle('msg_body--emoji-only', !!detail.emoji_only);
        body.classList.toggle('msg_body--no-bubble', !!detail.emoji_only);
      }

      var tag = node.querySelector('[data-edited-tag]');
      if (tag) {
        tag.classList.remove('hidden');
      }

      // Keep the swipe/reply preview text in sync with the edited body so a
      // later reply/swipe quotes the new text. Derive it from the rendered
      // HTML (no raw body on the recipient's channel).
      if (textRegion) {
        var previewText = (textRegion.textContent || '').trim().slice(0, 80);
        node.setAttribute('data-msg-preview', previewText);
        if (window.jQuery) { window.jQuery(node).data('msg-preview', previewText); }
      }

      // Refresh the inline-editor prefill on the sender's other devices (the
      // raw body ships only on the sender's own channel).
      if (typeof detail.body === 'string' && node.hasAttribute('data-edit-source')) {
        node.setAttribute('data-edit-source', detail.body);
      }
    }
  }

  // Refresh the thread-list snippet: an edit of the preview message rewrites
  // it server-side, but max_msg_id is unchanged so the delta poll would skip
  // the row. Force a rebuild, mirroring the reaction path.
  var fn = window.pm_view_mode === 'paged' ? window.update_pm_info_paged : window.update_pm_info;
  if (typeof fn === 'function') {
    fn(true);
  }
};

// WebSocket event listeners — trigger polls on relevant events.
// Gated to the PM page: this module ships in the global interpals bundle,
// but /app/messages/update has nothing to refresh elsewhere. Header badges
// on non-PM pages are kept in sync directly by websocket-client.js off the
// COUNTER_UPDATE payload.
if (document.querySelector('.privateMessages')) {
  document.addEventListener('thread:newmessage', function() {
    window.requestPoll();
  });

  document.addEventListener('ws:counter-update', function(e) {
    if (counterUpdateNeedsPoll(e.detail)) {
      window.requestPoll();
    }
  });

  document.addEventListener('thread:deleted', function(e) {
    if (e.detail && e.detail.thread_id) {
      window.removeThreadRow(e.detail.thread_id);
    }
  });

  document.addEventListener('thread:recalled', function(e) {
    if (e.detail && e.detail.message_id) {
      window.applyMessageRecallRemoval(e.detail.message_id, e.detail.thread_id);
    }
  });

  document.addEventListener('thread:edited', function(e) {
    if (e.detail && e.detail.message_id) {
      window.applyMessageEditUpdate(e.detail);
    }
  });

  // The same frame reaches the reader's other devices: only the contact's
  // read moves the viewer's own messages to seen. A hiding contact's frame
  // is withheld server-side.
  document.addEventListener('thread:viewed', function(e) {
    var d = e.detail || {};
    // With no signed-in id the reader's own-device frame is indistinguishable
    // from the contact's, so no frame is applied.
    var me = window.pmCurrentUserId();
    if (!me || !d.thread_id || !d.viewer_uid || d.viewer_uid === me) return;
    window.applyContactRead(d.thread_id, d.target_last_read_msg_id);
  });

  // Every connect polls, the first one included, so the inbox syncs when the
  // socket opens instead of on the pending fallback timer.
  document.addEventListener('ws:connected', function() {
    _pollMode = 'ws-gated';
    // The page render and a dropped socket both miss counter frames.
    _pmNewCnt = null;
    window.requestPoll();
  });

  document.addEventListener('ws:disconnected', function() {
    _pollMode = 'fallback';
    // Switch to fallback polling immediately
    window.run_update_process();
  });

  // The poll that requestPoll issues ends in finishPoll, which resumes the
  // mode-interval schedule.
  document.addEventListener('visibilitychange', function() {
    if (document.hidden) {
      clearTimeout(_safetyNetTimer);
    } else {
      window.requestPoll();
    }
  });
}

window.update_member_stats_messages = function(member_stats) {
  // Visual count display for the /messages page header badges. Title-flash
  // for counter changes lives in websocket-client.js (driven by the
  // ws:counter-update event), so this function only writes the badge text.
  function setCountRaw(stats, rawId, displayId) {
    var rawValue = stats[rawId] || '';
    var newValue = parseInt(rawValue) || 0;

    // Display "99+" for counts greater than 99, otherwise show the actual number
    var displayValue = rawValue;
    if (newValue > 99) {
      displayValue = '99+';
    }

    $('#' + displayId).text(displayValue ? '(+' + displayValue + ')' : '');
  }

  setCountRaw(member_stats, 'pmNewCnt_raw', 'pmNewCnt');
  setCountRaw(member_stats, 'wpNewCnt_raw', 'wpNewCnt');
  setCountRaw(member_stats, 'frNewCnt_raw', 'frNewCnt');
  setCountRaw(member_stats, 'phNewCnt_raw', 'phNewCnt');

  if (window.enable_notifs) {
    window.showNotifications(member_stats.msgs);
  }
  window.cur_ts = member_stats.ts;
};
